import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, ArrowLeft, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Seo } from "../components/Seo";
import { FadeUp } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { SERVICES, getService } from "../data/services";
import { SITE, waLink } from "../data/site";
import { track } from "../lib/analytics";

const FORM_PRODUCT = {
    "cartao-de-visita": "Cartão de visita",
    panfletos: "Panfleto",
    placas: "Placa",
    "banners-e-faixas": "Banner",
    adesivos: "Adesivo",
    "camisetas-personalizadas": "Camiseta",
    uniformes: "Uniforme",
    fachadas: "Fachada",
    "envelopamento-automotivo": "Envelopamento",
};

export default function ServicePage() {
    const { slug } = useParams();
    const service = getService(slug);

    useEffect(() => {
        if (service) track("service_view", { service: slug });
    }, [slug, service]);

    if (!service) return <Navigate to="/" replace />;

    const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
    const waMsg = `Olá! Gostaria de orçar: ${service.name}. Vim pelo site da Imprima Aki.`;

    return (
        <main className="bg-ink">
            <Seo title={service.seoTitle} description={service.seoDesc} />

            <section data-testid="service-hero" className="grain relative overflow-hidden pt-32 sm:pt-40 pb-14 text-white">
                <div className="hero-grid-bg absolute inset-0" />
                <div className="container-x relative">
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <Link
                            to="/"
                            data-testid="service-back-link"
                            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors hover:text-brand"
                        >
                            <ArrowLeft size={13} />
                            Todos os serviços
                        </Link>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.04]"
                    >
                        {service.name} em <span className="text-brand">Campinas</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-mist"
                    >
                        {service.short} Atendemos empresas, comércios e profissionais de Campinas e região.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.35 }}
                        className="mt-8 flex flex-wrap gap-4"
                    >
                        <a
                            href={waLink(waMsg)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("whatsapp_click", { location: "service_hero", service: slug })}
                            data-testid="service-cta-whatsapp"
                            className="group inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-brand-dark"
                        >
                            <MessageCircle size={18} fill="currentColor" />
                            Pedir orçamento
                        </a>
                        <a
                            href={`tel:+55${SITE.whatsapp.slice(2)}`}
                            data-testid="service-phone"
                            className="inline-flex items-center rounded-full border border-white/20 px-8 py-4 text-sm font-bold text-white transition-colors hover:border-brand hover:text-brand"
                        >
                            {SITE.phoneDisplay}
                        </a>
                    </motion.div>
                </div>
            </section>

            <section className="relative bg-ink pb-20 text-white">
                <div className="container-x">
                    <motion.div
                        initial={{ clipPath: "inset(0 0 100% 0)" }}
                        whileInView={{ clipPath: "inset(0 0 0% 0)" }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden rounded-2xl border border-line"
                    >
                        <img
                            src={service.image}
                            alt={`${service.name} em Campinas — IMPRIMA AKI`}
                            data-testid="service-image"
                            loading="lazy"
                            className="aspect-[21/9] w-full object-cover"
                        />
                    </motion.div>
                </div>
            </section>

            <section className="bg-paper py-16 sm:py-24">
                <div className="container-x grid gap-12 lg:grid-cols-2">
                    <FadeUp>
                        <p className="section-tag">O serviço</p>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-ink leading-tight">
                            Produção profissional, do arquivo ao acabamento.
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-smoke">{service.intro}</p>
                        <a
                            href={waLink(waMsg)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("whatsapp_click", { location: "service_body", service: slug })}
                            data-testid="service-body-cta"
                            className="group mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand"
                        >
                            Orçar pelo WhatsApp
                            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        <div className="rounded-2xl border border-[#E9E9E9] bg-white p-7 sm:p-9">
                            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">O que produzimos</p>
                            <ul className="mt-6 space-y-4">
                                {service.items.map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-ink">
                                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand">
                                            <Check size={13} strokeWidth={3} />
                                        </span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section className="bg-paper pb-16 sm:pb-24">
                <div className="container-x grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <FadeUp>
                            <p className="section-tag">Dúvidas sobre {service.name.toLowerCase()}</p>
                            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">
                                Perguntas frequentes
                            </h2>
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-7">
                        <FadeUp delay={0.1}>
                            <Accordion type="single" collapsible className="w-full space-y-3">
                                {service.faqs.map((item, i) => (
                                    <AccordionItem
                                        key={i}
                                        value={`sfaq-${i}`}
                                        data-testid={`service-faq-${i}`}
                                        className="rounded-xl border border-[#E9E9E9] bg-white px-6 data-[state=open]:border-brand/40"
                                    >
                                        <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-ink hover:text-brand hover:no-underline">
                                            {item.q}
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-5 text-sm leading-relaxed text-smoke">
                                            {item.a}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <section data-testid="service-form-section" className="border-t border-[#ECECEC] bg-paper py-16 sm:py-24">
                <div className="container-x">
                    <FadeUp>
                        <div className="mx-auto max-w-3xl text-center">
                            <p className="section-tag">Orçamento sem compromisso</p>
                            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-ink">
                                Peça agora o orçamento de {service.name.toLowerCase()}
                            </h2>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-[#E9E9E9] bg-white p-6 sm:p-10 shadow-[0_30px_80px_-40px_rgba(13,13,13,0.25)]">
                            <QuoteForm defaultProduct={FORM_PRODUCT[slug] || ""} />
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section className="bg-ink py-14 text-white">
                <div className="container-x flex flex-col items-start gap-6">
                    <p className="section-tag">Outros serviços</p>
                    <div className="flex flex-wrap gap-3">
                        {others.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/servicos/${s.slug}`}
                                data-testid={`service-related-${s.slug}`}
                                className="rounded-full border border-line px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-mist transition-colors hover:border-brand hover:text-brand"
                            >
                                {s.name}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
